import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Spin } from 'antd';
import { SplitShell } from '../layout/split/SplitShell';
import styles from '../layout/split/split.module.css';
import { ApiError, accountsApi } from '../api';
import type { MembershipPickerResponse } from '@velora/contracts';

/**
 * /select — profile / institution picker.
 * I-04: skipped when the caller has exactly one active membership.
 */
export function SelectPage() {
  return <SplitShell><SelectPicker /></SplitShell>;
}

function SelectPicker() {
  const navigate = useNavigate();
  const [data, setData] = useState<MembershipPickerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    accountsApi.getMemberships().then(setData).catch((err) => {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    });
  }, [navigate]);

  async function pick(id: string) {
    if (busyId) return;
    setBusyId(id); setError(null);
    try {
      const res = await accountsApi.pickMembership({ membership_id: id });
      window.location.href = res.redirect_to;
    } catch (err) {
      setError(err instanceof ApiError
        ? (err.body as { message?: string } | null)?.message ?? err.message
        : err instanceof Error ? err.message : String(err));
    } finally { setBusyId(null); }
  }

  if (!data && !error) return <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;

  return (
    <div>
      <div className={styles.stepHead}>
        <h2 className={styles.stepTitle}>Pick where to go</h2>
        <p className={styles.stepSubtitle}>
          {data && `Hi ${data.person.name.split(' ')[0]} — you have ${data.memberships.length} places on file.`}
        </p>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 22 }} />}

      {data && (
        <div style={{ display: 'grid', gap: 10, marginTop: 22 }}>
          {data.memberships.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => pick(m.id)}
              disabled={busyId !== null}
              style={{
                appearance: 'none', border: '1px solid #e6e6e9',
                background: busyId === m.id ? '#f3f6fb' : '#fff',
                borderRadius: 8, padding: '14px 16px',
                textAlign: 'left', cursor: busyId ? 'wait' : 'pointer',
                display: 'flex', gap: 12, alignItems: 'center',
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 8,
                background: '#eaf3fb', color: '#2f6ab0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700,
              }}>
                {m.institution.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{m.institution.name}</div>
                <div style={{ color: '#5b6478', fontSize: 12 }}>
                  {m.organization.name} · {m.institution.type} · {m.role}
                </div>
              </div>
              {busyId === m.id ? <Spin size="small" /> : <span style={{ color: '#8a94a6' }}>→</span>}
            </button>
          ))}
        </div>
      )}

      <p className={styles.altAction} style={{ marginTop: 24 }}>
        Not you? <Button size="small" type="text" onClick={async () => {
          await accountsApi.logout().catch(() => {});
          navigate('/login');
        }}>Sign out</Button>
      </p>
    </div>
  );
}
